/**
 * FieldConfigContext for UI-Model-1
 *
 * Manages field configurations, default settings, and field type mappings.
 * Contains field default configurations extracted from EditorContext.
 *
 * Line limit: ~100 lines
 */

import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { FieldConfigContextValue } from '../types/context.types';

// ============================================================================
// Field Type Defaults
// ============================================================================

export type FieldType = 'multichoice' | 'text' | 'integer' | 'decimal' | 'boolean' | 'range' | 'fraction';
export type DisplayType = 'dropdown' | 'radio' | 'chips' | 'text' | 'toggle' | 'slider' | 'input';

export interface FieldDefaultConfig {
  displayTypes: DisplayType[];
  defaultDisplayType: DisplayType;
  supportsRequired: boolean;
  supportsValidation: boolean;
  labelVisible: boolean;
}

const FIELD_DEFAULTS: Record<FieldType, FieldDefaultConfig> = {
  multichoice: {
    displayTypes: ['dropdown', 'radio', 'chips'],
    defaultDisplayType: 'dropdown',
    supportsRequired: true,
    supportsValidation: true,
    labelVisible: true,
  },
  text: {
    displayTypes: ['text'],
    defaultDisplayType: 'text',
    supportsRequired: true,
    supportsValidation: true,
    labelVisible: true,
  },
  integer: {
    displayTypes: ['input', 'slider'],
    defaultDisplayType: 'input',
    supportsRequired: true,
    supportsValidation: true,
    labelVisible: true,
  },
  decimal: {
    displayTypes: ['input', 'slider'],
    defaultDisplayType: 'input',
    supportsRequired: true,
    supportsValidation: true,
    labelVisible: true,
  },
  boolean: {
    displayTypes: ['toggle', 'radio', 'dropdown'],
    defaultDisplayType: 'toggle',
    supportsRequired: false,
    supportsValidation: false,
    labelVisible: true,
  },
  range: {
    displayTypes: ['slider', 'input'],
    defaultDisplayType: 'slider',
    supportsRequired: true,
    supportsValidation: true,
    labelVisible: true,
  },
  fraction: {
    displayTypes: ['input'],
    defaultDisplayType: 'input',
    supportsRequired: true,
    supportsValidation: true,
    labelVisible: true,
  },
};

// ============================================================================
// Context
// ============================================================================

const FieldConfigContext = createContext<FieldConfigContextValue | null>(null);

export const FieldConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const getFieldDefaults = useCallback((fieldType: FieldType): FieldDefaultConfig => {
    return FIELD_DEFAULTS[fieldType] || FIELD_DEFAULTS.text;
  }, []);

  const getDisplayTypes = useCallback((fieldType: FieldType): DisplayType[] => {
    return FIELD_DEFAULTS[fieldType]?.displayTypes || ['text'];
  }, []);

  const getDefaultDisplayType = useCallback((fieldType: FieldType): DisplayType => {
    return FIELD_DEFAULTS[fieldType]?.defaultDisplayType || 'text';
  }, []);

  const isValidDisplayType = useCallback((fieldType: FieldType, displayType: DisplayType): boolean => {
    const validTypes = FIELD_DEFAULTS[fieldType]?.displayTypes || [];
    return validTypes.includes(displayType);
  }, []);

  const createDefaultFieldConfiguration = useCallback((fieldType: FieldType, fieldName: string) => {
    const defaults = getFieldDefaults(fieldType);
    return {
      label: { visible: defaults.labelVisible, text: fieldName },
      displayType: defaults.defaultDisplayType,
      required: false,
      showValidation: defaults.supportsValidation,
    };
  }, [getFieldDefaults]);

  const value = useMemo<FieldConfigContextValue>(
    () => ({
      fieldDefaults: FIELD_DEFAULTS,
      getFieldDefaults,
      getDisplayTypes,
      getDefaultDisplayType,
      isValidDisplayType,
      createDefaultFieldConfiguration,
    }),
    [getFieldDefaults, getDisplayTypes, getDefaultDisplayType, isValidDisplayType, createDefaultFieldConfiguration]
  );

  return (
    <FieldConfigContext.Provider value={value}>
      {children}
    </FieldConfigContext.Provider>
  );
};

export const useFieldConfig = (): FieldConfigContextValue => {
  const context = useContext(FieldConfigContext);
  if (!context) {
    throw new Error('useFieldConfig must be used within a FieldConfigProvider');
  }
  return context;
};

export default FieldConfigContext;
